import { useEffect, Suspense, lazy } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Routes, Route, Navigate } from "react-router-dom";
import { ToastContainer, Zoom } from "react-toastify";
import { AppConstants } from "./AppConstants";
import { AppDispatch, getDataFromLocalStorage, RootState } from "./redux/internal";
import { SliceCallState } from "./slices/sliceCallState";
import { PermissionsGate } from "./components/authentication/PermissionsGate";
import { AuthorizeUser } from "./components/authentication/AuthorizeUser";
import { ForgottenPassword } from "./pages/forgottenPassword/ForgottenPassword";
import { Login } from "./pages/login/Login";
import { Logout } from "./components/authentication/Logout";
import { SetPassword } from "./pages/setPassword/SetPassword";
import { Signin } from "./components/authentication/Signin";
import { BulkUpload } from "./pages/bulkUpload/BulkUpload";
import { initializeGoogleAnalytics } from "./utils/google-analytics";
import { ReadOnlyUser } from "./utils/readOnlyUser";
import "./assets/_app.scss";
import "./assets/toastify.scss";

const UserManagement = lazy(() => import("./UserManagementRoutes"));

function App() {
  const dispatch = useDispatch<AppDispatch>();
  const { token, loading, decodedToken } = useSelector((state: RootState) => state.auth);
  useEffect(() => {
    if (!token && loading === SliceCallState.pending) {
      dispatch(getDataFromLocalStorage());
    } else if (token && (loading === SliceCallState.idle || loading === SliceCallState.succeeded) && decodedToken) {
      initializeGoogleAnalytics(decodedToken);
      ReadOnlyUser();
    }
  }, [dispatch, loading, token, decodedToken]);

  return (
    <Routes>
      <Route path="*" element={<Navigate to="/" />} />
      <Route path="/" element={<Navigate to="/user-management" />} />
      <Route path="/login" element={<Login />} />
      <Route path="/signin" element={<Signin />} />
      <Route path="/logout" element={<Logout />} />
      <Route path="/authorize-user" element={<AuthorizeUser />} />
      <Route path="/forgotten-password" element={<ForgottenPassword />} />
      <Route path="/set-password" element={<SetPassword />} />
      <Route path="bulk-upload" element={<BulkUpload />} />
      <Route
        path="/user-management/*"
        element={
          <PermissionsGate>
            <UserManagement />
          </PermissionsGate>
        }
      ></Route>
    </Routes>
  );
}

// here app catches the suspense from page in case translations are not yet loaded
export default function WrappedApp() {
  return (
    <div className="app">
      <Suspense fallback="...is loading">
        <App />
        <ToastContainer
          autoClose={AppConstants.toastAutoClose}
          newestOnTop={false}
          closeOnClick
          rtl={false}
          hideProgressBar
          transition={Zoom}
          className="error-message"
        />
      </Suspense>
    </div>
  );
}
